"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Authorization = void 0;
const perms_1 = require("./config/perms");
const roles_1 = require("./roles");
/**
 * Wrapper for the Authorization status that make them easier to use.
 * For use from React primarily.
 * Would usually be instantiated on each call to render.
 *
 * The hash function is provided for useEffect or similar
 * For hooks that need to be run any time a user's auth status changes
 */
class Authorization {
    constructor(props) {
        /**
         * If the user has a set of roles
         */
        this.hasRoles = (...roles) => !roles.find((role) => !this._roles.includes(role));
        /**
         * If the user currently has a role
         */
        this.hasRole = (role) => this._roles.includes(role);
        /**
         * If the user has ANY of a set of roles (OR operation)
         */
        this.hasAnyRole = (...roles) => !!roles.find((role) => this._roles.includes(role));
        /**
         * Creates the query vars for a GraphQL query that's had auth vars
         * automatically added to it via addAuth.
         *
         * Merge result vars with other meaningful input vars before execuing query
         */
        this.addVals = (query, baseVars = {}) => {
            const op = query.definitions.find((def) => def.kind === "OperationDefinition");
            const varValues = {};
            if (op) {
                const authVars = op.variableDefinitions
                    ?.map((varDef) => varDef.variable.name.value)
                    ?.filter((varName) => varName.startsWith("__auth__"));
                if (authVars) {
                    for (const varName of authVars) {
                        const [, , typeName, fieldName] = varName.split("__");
                        const roles = perms_1.QueryFieldPermissions[typeName]?.[fieldName];
                        if (roles) {
                            varValues[varName] = this.hasAnyRole(...roles);
                        }
                    }
                }
            }
            return { ...varValues, ...baseVars };
        };
        if (props.authed) {
            this._authed = true;
            this._accessToken = props.accessToken;
            this._userInfo = props.userInfo;
            if (props.userInfo.roles !== undefined) {
                // due to how JWTs are encoded, single values are not arrays
                this._roles = (0, roles_1.fromArray)(Array.isArray(props.userInfo.roles)
                    ? props.userInfo.roles
                    : [props.userInfo.roles]);
            }
            else {
                this._roles = [];
            }
        }
        else {
            this._authed = false;
            this._accessToken = null;
            this._userInfo = null;
            this._roles = [];
        }
    }
    /**
     * Whether the user is currently authenticated
     */
    get authed() {
        return this._authed;
    }
    /**
     * The current access token for the user
     */
    get token() {
        return this._accessToken;
    }
    /**
     * Returns the UserInfo object directly
     */
    get info() {
        return this._userInfo;
    }
    /**
     * Returns the user's full set of roles
     * Proxy for userInfo.roles (convenience method)
     */
    get roles() {
        return this._roles;
    }
    /**
     * Returns a unique set of primitives representing the auth state.
     * Intended for change detection hashing.
     * Can be used with React useEffect hook or similar.
     */
    get deps() {
        return [
            this._authed,
            this._userInfo?.type ?? null,
            this._userInfo?.id ?? null,
            this._roles,
        ];
    }
}
exports.Authorization = Authorization;
